import { Box, Typography } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import { ClassDetailsNoSectionsQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import {
  TeacherNameAndRatings,
  TeacherVideoModal
} from "@outschool/ui-components-website";
import React, { useState } from "react";

import { useIsActivitySavedByCurrentUser } from "../../saving/graphql/hooks";
import { SaveButton } from "../../saving/SaveButton";
import { AskAQuestionButton } from "../ctas/AskAQuestionButton";
import ActivityPhoto from "./ActivityPhoto";
import ActivityShareMenu from "./ActivityShareMenu";
import { EsaEligibleChips } from "./EsaEligibleChips";
import { WatchMyTeacherVideoLink } from "./WatchMyTeacherVideoLink";

interface DesktopActivityHeaderProps {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  onClickViewTeacher?: (event: React.SyntheticEvent) => void;
  onClickViewReviews?: (event: React.SyntheticEvent) => void;
}

const MAIN_IMAGE_HEIGHT = 179;
const MAIN_IMAGE_WIDTH = 344;

function DesktopActivityHeader({
  activity,
  leader,
  onClickViewTeacher,
  onClickViewReviews
}: DesktopActivityHeaderProps) {
  const isActivitySavedByCurrentUser =
    useIsActivitySavedByCurrentUser(activity);
  const { currentUser } = useSession();
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\DesktopActivityHeader"
  );
  const imageUrl = React.useMemo(() => {
    return FilestackUrls.imageUrl(activity.details.photo, {
      w: MAIN_IMAGE_WIDTH,
      h: MAIN_IMAGE_HEIGHT,
      fit: "crop"
    });
  }, [activity.details.photo]);

  const showTeacherVideoLink = !activity?.video && !!activity?.leader?.video;
  const [teacherVideoModalOpen, setTeacherVideoModalOpen] = useState(false);

  const MessageTeacherButton = (
    <AskAQuestionButton activity={activity} leader={leader} />
  );

  return (
    <Box>
      <TeacherVideoModal
        isOpen={teacherVideoModalOpen}
        onClose={() => setTeacherVideoModalOpen(false)}
        teacher={leader}
        MessageTeacherButton={MessageTeacherButton}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem"
        }}
      >
        <EsaEligibleChips activity={activity} />
      </Box>
      <Typography variant="h1" id="about">
        {activity.titleTranslated}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "2.4rem",
          marginBottom: 33
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: "300px"
          }}
        >
          <Typography
            component="div"
            sx={{ marginTop: 24, wordWrap: "break-word" }}
          >
            {activity.summaryTranslated}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "1em",
              marginY: 12
            }}
          >
            <SaveButton
              variant="link"
              isSet={isActivitySavedByCurrentUser}
              activity={activity}
              disabled={false}
              size="small"
            />
            <ActivityShareMenu
              user={currentUser}
              activity={activity}
              label={t("Share")}
              size="small"
            />
          </Box>
          <TeacherNameAndRatings
            leader={leader}
            onClickViewTeacher={onClickViewTeacher}
            onClickViewReviews={onClickViewReviews}
          />
          {showTeacherVideoLink && (
            <WatchMyTeacherVideoLink
              activity={activity}
              openModal={() => setTeacherVideoModalOpen(true)}
            />
          )}
        </Box>
        <Box sx={{ flexShrink: 0, marginTop: 24 }}>
          <ActivityPhoto
            activity={activity}
            imageUrl={imageUrl}
            imageWidth={MAIN_IMAGE_WIDTH}
            imageHeight={MAIN_IMAGE_HEIGHT}
            containerHeight={(300 * MAIN_IMAGE_HEIGHT) / MAIN_IMAGE_WIDTH}
          />
        </Box>
      </Box>
    </Box>
  );
}
export default React.memo(DesktopActivityHeader);
